import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import styled from 'styled-components';
import { Formik, Field, ErrorMessage as FormikErrorMessage, FormikHelpers } from 'formik';
import * as Yup from 'yup';
import Layout from '../components/layout/Layout';
import { sendStandSaleContract } from '../services/contactService';
import { theme, media } from '../styles/theme';
import {
  PageContainer,
  PageTitle,
  Text,
  Form,
  FormGroup,
  Label,
  Input,
  TextArea,
  Button,
  Alert,
  FormContainer,
  ErrorText,
} from '../styles/components';

const StandContainer = styled(PageContainer)`
  max-width: 640px;
`;

const FieldRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 2fr;
  gap: ${theme.spacing.sm};

  ${media.mobile} {
    grid-template-columns: 1fr;
    gap: 0;
  }
`;

const SectionHeading = styled.h2`
  font-size: ${theme.fontSizes.lg};
  margin: ${theme.spacing.md} 0 ${theme.spacing.sm};

  &:first-child {
    margin-top: 0;
  }
`;

const Hint = styled.span`
  display: block;
  font-size: ${theme.fontSizes.sm};
  opacity: 0.8;
  margin-top: ${theme.spacing.xs};
`;

const CheckboxLabel = styled.label`
  display: flex;
  gap: ${theme.spacing.sm};
  align-items: flex-start;
  cursor: pointer;

  input {
    width: 1.25rem;
    height: 1.25rem;
    margin-top: 0.15rem;
    flex-shrink: 0;
  }
`;

// Hidden from people, but bots filling every field will populate it
const Honeypot = styled.div`
  position: absolute;
  left: -10000px;
  width: 1px;
  height: 1px;
  overflow: hidden;
`;

const FieldError: React.FC<{ name: string }> = ({ name }) => (
  <FormikErrorMessage name={name} component={ErrorText} />
);

interface StandSaleFormValues {
  name: string;
  email: string;
  phone: string;
  street: string;
  postalCode: string;
  city: string;
  cardCount: string;
  description: string;
  accountNumber: string;
  confirmed: boolean;
  website: string;
}

const initialValues: StandSaleFormValues = {
  name: '',
  email: '',
  phone: '',
  street: '',
  postalCode: '',
  city: '',
  cardCount: '',
  description: '',
  accountNumber: '',
  confirmed: false,
  website: '',
};

const validationSchema = Yup.object({
  name: Yup.string().trim().max(100, 'Maks 100 tegn').required('Skriv inn fullt navn'),
  email: Yup.string().trim().email('Ugyldig e-postadresse').max(150).required('Skriv inn e-post'),
  phone: Yup.string()
    .trim()
    .matches(/^\+?[\d\s]{8,15}$/, 'Ugyldig telefonnummer')
    .required('Skriv inn telefonnummer'),
  street: Yup.string().trim().max(150, 'Maks 150 tegn').required('Skriv inn adresse'),
  postalCode: Yup.string().trim().matches(/^\d{4}$/, 'Postnummer må være 4 siffer').required('Skriv inn postnummer'),
  city: Yup.string().trim().max(80, 'Maks 80 tegn').required('Skriv inn poststed'),
  cardCount: Yup.number()
    .typeError('Skriv inn et tall')
    .integer('Skriv inn et helt tall')
    .positive('Antall må være større enn 0')
    .required('Skriv inn omtrentlig antall kort'),
  description: Yup.string().max(2000, 'Maks 2000 tegn'),
  accountNumber: Yup.string()
    .trim()
    .matches(/^\d{4}[ .]?\d{2}[ .]?\d{5}$/, 'Kontonummer må være 11 siffer')
    .required('Skriv inn kontonummer'),
  confirmed: Yup.boolean().oneOf([true], 'Du må bekrefte før du sender'),
});

const StandSalePage: React.FC = () => {
  const [submitted, setSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState('');

  const handleSubmit = async (
    values: StandSaleFormValues,
    { setSubmitting, resetForm }: FormikHelpers<StandSaleFormValues>
  ) => {
    setSubmitError('');
    try {
      await sendStandSaleContract({
        name: values.name.trim(),
        email: values.email.trim(),
        phone: values.phone.trim(),
        street: values.street.trim(),
        postalCode: values.postalCode.trim(),
        city: values.city.trim(),
        cardCount: Number(values.cardCount),
        description: values.description.trim(),
        accountNumber: values.accountNumber.trim(),
        website: values.website,
      });
      resetForm();
      setSubmitted(true);
      window.scrollTo(0, 0);
    } catch {
      setSubmitError('Noe gikk galt under innsendingen. Prøv igjen, eller si ifra til oss på standen.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Layout>
      <Helmet>
        <title>Salg på stand | Tryllepapp</title>
        <meta name="robots" content="noindex, nofollow" />
        <link rel="canonical" href="https://selg.tryllepapp.no/selge-pa-stand" />
      </Helmet>
      <StandContainer>
        <PageTitle>Salg på stand</PageTitle>

        {submitted ? (
          <>
            <Alert variant="success">
              Takk! Vi har mottatt skjemaet og sender deg kjøpekontrakten for digital signering.
            </Alert>
            <Button type="button" variant="primary" size="large" fullWidth onClick={() => setSubmitted(false)}>
              Ny kunde
            </Button>
          </>
        ) : (
          <>
            <Text>
              Selger du kort til oss på stand? Fyll ut skjemaet, så sender vi deg kjøpekontrakten for digital
              signering. Kjøpesummen fyller vi inn sammen med deg.
            </Text>

            {submitError && <Alert variant="danger">{submitError}</Alert>}

            <FormContainer>
              <Formik initialValues={initialValues} validationSchema={validationSchema} onSubmit={handleSubmit}>
                {({ handleSubmit: formikSubmit, isSubmitting }) => (
                  <Form onSubmit={formikSubmit} noValidate>
                    <SectionHeading>Dine opplysninger</SectionHeading>

                    <FormGroup>
                      <Label htmlFor="name">Fullt navn</Label>
                      <Field as={Input} id="name" name="name" autoComplete="name" />
                      <FieldError name="name" />
                    </FormGroup>

                    <FormGroup>
                      <Label htmlFor="email">E-post</Label>
                      <Field as={Input} id="email" name="email" type="email" inputMode="email" autoComplete="email" />
                      <Hint>Kjøpekontrakten sendes hit for signering.</Hint>
                      <FieldError name="email" />
                    </FormGroup>

                    <FormGroup>
                      <Label htmlFor="phone">Telefon</Label>
                      <Field as={Input} id="phone" name="phone" type="tel" inputMode="tel" autoComplete="tel" />
                      <FieldError name="phone" />
                    </FormGroup>

                    <FormGroup>
                      <Label htmlFor="street">Adresse</Label>
                      <Field as={Input} id="street" name="street" autoComplete="street-address" />
                      <FieldError name="street" />
                    </FormGroup>

                    <FieldRow>
                      <FormGroup>
                        <Label htmlFor="postalCode">Postnummer</Label>
                        <Field
                          as={Input}
                          id="postalCode"
                          name="postalCode"
                          inputMode="numeric"
                          autoComplete="postal-code"
                          maxLength={4}
                        />
                        <FieldError name="postalCode" />
                      </FormGroup>
                      <FormGroup>
                        <Label htmlFor="city">Poststed</Label>
                        <Field as={Input} id="city" name="city" autoComplete="address-level2" />
                        <FieldError name="city" />
                      </FormGroup>
                    </FieldRow>

                    <SectionHeading>Hva selger du?</SectionHeading>

                    <FormGroup>
                      <Label htmlFor="cardCount">Omtrentlig antall kort</Label>
                      <Field as={Input} id="cardCount" name="cardCount" inputMode="numeric" />
                      <FieldError name="cardCount" />
                    </FormGroup>

                    <FormGroup>
                      <Label htmlFor="description">Beskrivelse (valgfritt)</Label>
                      <Field
                        as={TextArea}
                        id="description"
                        name="description"
                        placeholder="F.eks. sett, spesielle kort eller annet vi bør vite"
                      />
                      <FieldError name="description" />
                    </FormGroup>

                    <SectionHeading>Utbetaling</SectionHeading>

                    <FormGroup>
                      <Label htmlFor="accountNumber">Kontonummer</Label>
                      <Field
                        as={Input}
                        id="accountNumber"
                        name="accountNumber"
                        inputMode="numeric"
                        placeholder="1234 56 78901"
                      />
                      <FieldError name="accountNumber" />
                    </FormGroup>

                    <Honeypot aria-hidden="true">
                      <label htmlFor="website">Nettside</label>
                      <Field id="website" name="website" tabIndex={-1} autoComplete="off" />
                    </Honeypot>

                    <FormGroup>
                      <CheckboxLabel>
                        <Field type="checkbox" name="confirmed" />
                        <span>Jeg bekrefter at opplysningene stemmer og at jeg eier kortene.</span>
                      </CheckboxLabel>
                      <FieldError name="confirmed" />
                    </FormGroup>

                    <Button type="submit" variant="secondary" size="large" fullWidth disabled={isSubmitting}>
                      {isSubmitting ? 'Sender...' : 'Send skjema'}
                    </Button>
                  </Form>
                )}
              </Formik>
            </FormContainer>
          </>
        )}
      </StandContainer>
    </Layout>
  );
};

export default StandSalePage;
